import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, LayoutTemplate, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { FALLBACK_TEMPLATES } from './templates';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dialog for picking an outline template — either applies the static skeleton
 * from the chosen template, or asks Claude to generate a Bible-anchored
 * outline for the current topic using that template's structure.
 */
const SermonTemplatesDialog = ({
  open,
  onOpenChange,
  language,
  topic,
  notes,
  onApplyOutline,   // (points) => void
}) => {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState(FALLBACK_TEMPLATES);
  const [selectedId, setSelectedId] = useState('default');
  const [generatingId, setGeneratingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/sermons/templates`)
      .then((r) => {
        if (cancelled) return;
        if (Array.isArray(r.data?.templates)) setTemplates(r.data.templates);
      })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, [open]);

  const applySkeleton = (tmpl) => {
    const list = (language === 'es' && tmpl.points_es) ? tmpl.points_es : (tmpl.points || []);
    const points = list.map((title, i) => ({
      id: Date.now() + i,
      title: String(title),
      content: '',
    }));
    if (points.length === 0) {
      toast.error(t('sermon.emptyTemplate'));
      return;
    }
    onApplyOutline(points);
    onOpenChange(false);
    toast.success(t('sermon.templateApplied'));
  };

  const generateWithAI = async (tmpl) => {
    if (!topic || topic.trim().length < 2) {
      toast.error(t('sermon.enterTopicFirstTmpl'));
      return;
    }
    setGeneratingId(tmpl.id);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/sermons/outline/generate`,
        {
          topic,
          notes: notes || '',
          template_id: tmpl.id,
          language,
        },
        { withCredentials: true },
      );
      const points = (res.data?.points || []).map((p, i) => ({
        id: Date.now() + i,
        title: p.title || '',
        content: p.content || '',
      }));
      if (points.length === 0) {
        toast.error(t('sermon.aiNoPoints'));
        return;
      }
      onApplyOutline(points);
      onOpenChange(false);
      toast.success(t('sermon.outlineGenerated'));
    } catch (err) {
      console.warn('Outline generation failed:', err);
      toast.error(t('sermon.generationFailed'));
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="sermon-templates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutTemplate className="w-5 h-5 text-purple-600" />
            {t('sermon.outlineTemplatesTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('sermon.templatesDesc')}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto space-y-2">
          {templates.map((tmpl) => {
            const name = (language === 'es' ? tmpl.name_es : tmpl.name_en) || tmpl.id;
            const desc = (language === 'es' ? tmpl.description_es : tmpl.description_en) || '';
            const isActive = tmpl.id === selectedId;
            const isGenerating = generatingId === tmpl.id;
            return (
              <div
                key={tmpl.id}
                className={`p-3 rounded-md border text-sm transition-colors ${
                  isActive
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
                data-testid={`sermon-template-${tmpl.id}`}
                onClick={() => setSelectedId(tmpl.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold">{name}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{desc}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); applySkeleton(tmpl); }}
                    data-testid={`sermon-template-apply-${tmpl.id}`}
                  >
                    {t('sermon.useTemplate')}
                  </Button>
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
                    disabled={isGenerating}
                    onClick={(e) => { e.stopPropagation(); generateWithAI(tmpl); }}
                    data-testid={`sermon-template-generate-${tmpl.id}`}
                  >
                    {isGenerating ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5 mr-1" />
                    )}
                    {t('sermon.generateWithAI')}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SermonTemplatesDialog;
